import React, { useEffect, useState } from 'react';
import { Typography, Box, Skeleton, Stack } from '@mui/material';
import moment from 'moment-timezone';
import _isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import { useFoodTypes } from 'src/constants';
import useGetDonationActivityLogs from 'src/hooks/useGetDonationActivityLogs';
import { ArrowRightAlt } from '@mui/icons-material';
import { Weight, formatDisplayDate } from 'careit';
import { describeDriverUnassign } from './driverUnassignEvent';

const icons = {
  add: '/static/icons/ic_log_add.svg',
  edit: '/static/icons/ic_log_edited.svg',
  van: '/static/icons/ic_log_van.svg',
  complete: '/static/icons/ic_log_complete.svg',
  cancelled: '/static/icons/ic_log_cancelled.svg',
  pencil: '/static/icons/ic_log_pencil.svg',
};
const iconsTypeMap = (type) => {
  switch (type) {
    case 'CREATED_DONATION':
    case 'RECEIVER_BULK_ASSIGN_BY_DONOR':
    case 'RECEIVER_ASSIGN_BY_DONOR':
    case 'DONATION_CONVERTED_TO_FOOD_RUN':
    case 'DRIVER_ASSIGN':
    case 'PARTIAL_RESCUE_WEIGHTS_INCREASED':
    case 'PARTIAL_RESCUE_NEW_FOOD_TYPE_ADDED':
      return icons.add;
    case 'COMPLETED_RESCUE':
      return icons.complete;
    case 'PARTIAL_RESCUE_ADJUST_ALLOCATION':
    case 'PARTIAL_RESCUE_BOUNDS_AUTO_ADJUSTED':
      return icons.pencil;
    case 'DONATION_CHANGED':
    case 'FOOD_RUN_RECIPIENT_CHANGED_RETROACTIVELY':
      return icons.edit;
    case 'RESCUE_CANCELLED_BY_RECEIVER':
      return icons.cancelled;
    default:
      return icons.van;
  }
};

const IconImg = styled('img')({
  width: 18,
  height: 18,
  filter: 'grayscale(1) saturate(0) brightness(0.8)',
  opacity: 0.75,
});

const DonationEventLogs = ({
  donationId,
  tz,
  isCurrentTeamDonor = true,
  recipientTeamId = null,
  rescueId,
}) => {
  const { t } = useTranslation('translation');
  const [data, setData] = useState({});
  const { user } = useAuth();
  const viewOrg =
    user?.account_type === 'ADMIN'
      ? PATH_DASHBOARD.admin.viewOrg
      : PATH_DASHBOARD.viewOrg;
  const foodTypes = useFoodTypes();
  const { data: events = [], isLoading } = useGetDonationActivityLogs({
    donationId,
    rescueId,
    enabled: !!donationId,
  });

  useEffect(() => {
    if (!Array.isArray(events) || events.length === 0) return;
    let NPOEVENTS = [
      'RECEIVER_BULK_ASSIGN_BY_DONOR',
      'RECEIVER_ASSIGN_BY_DONOR',
      'RESERVED',
      'DRIVER_ASSIGN',
      'DRIVER_UNASSIGN',
      'RESCUE_STARTED',
      'RESCUE_CANCELLED_BY_RECEIVER',
      'PARTIAL_RESCUE_WEIGHTS_INCREASED',
      'PARTIAL_RESCUE_NEW_FOOD_TYPE_ADDED',
    ];
    let donorEvents = [
      'PARTIAL_RESCUE_ADJUST_ALLOCATION',
      'PARTIAL_RESCUE_BOUNDS_AUTO_ADJUSTED',
    ];
    let filteredEvents = events.filter((event) => {
      if (isCurrentTeamDonor) {
        return true;
      } else {
        if (donorEvents.includes(event.activity_type)) {
          return false;
        }
        //ensure npo's cannot see each others events, only the events from the donor and their own organization
        // Super admin & corporate users can see all events
        if (
          NPOEVENTS.includes(event.activity_type) &&
          user.account_type !== 'ADMIN' &&
          !user.is_corporate
        ) {
          return event.assignee_team === recipientTeamId;
        } else {
          return true;
        }
      }
    });
    let processedEvents = filteredEvents
      .map(processEvent)
      .filter((item) => !!item.message);
    const data = {};
    processedEvents.forEach((item) => {
      data[moment.utc(item.created).tz(tz).format('YYYY-MM-DD')] = [];
    });
    Object.keys(data).forEach((key) => {
      data[key] = processedEvents.filter(
        (item) => moment.utc(item.created).tz(tz).format('YYYY-MM-DD') === key
      );
    });
    setData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    events,
    isCurrentTeamDonor,
    user?.is_corporate,
    user?.account_type,
    recipientTeamId,
    tz,
  ]);

  function formatWeightChanges(event) {
    const changes = [];

    if (event.deleted_available_rescue) {
      changes.push({
        change_from: 0,
        change_to: 0,
        isAvailable: true,
      });
    }

    const weightMappings = [
      { prefix: 'available_weight', label: 'available' },
      { prefix: 'min_weight', label: 'min-reservable-weight' },
      { prefix: 'max_weight', label: 'max-reservable-weight' },
      { prefix: 'reserved_weight', label: 'reserved' },
    ];

    for (const mapping of weightMappings) {
      const fromField = `${mapping.prefix}_change_from`;
      const toField = `${mapping.prefix}_change_to`;

      const change_from = event[fromField];
      const change_to = event[toField];
      if (
        change_from != null &&
        change_to != null &&
        change_from !== change_to
      ) {
        changes.push({
          change_from: Number(change_from),
          change_to: Number(change_to),
          label: mapping.label,
        });
      }
    }

    return changes;
  }
  const getActivityDrivers = (event) => {
    const onlyOneDriver = event.activity_rescue_drivers.length === 1;
    const idToSearch =
      event.activity_type === 'RESERVED'
        ? event.assignee_id
        : event.assignor_id;

    const selfDriver = event.activity_rescue_drivers.find(
      (d) => d.user_id === idToSearch
    );

    const driverNames = event.activity_rescue_drivers
      .map(
        (driver) =>
          `${driver.name}${
            idToSearch === driver.user_id ? ` (${t('donations.self')})` : ''
          }`
      )
      .join(', ');

    const listDrivers = (
      <>
        {' '}
        <Box component="span">{driverNames}</Box>
      </>
    );
    return {
      onlyOneDriver,
      selfDriver,
      listDrivers,
    };
  };
  const processEvent = (event) => {
    let message = null;
    const driverName =
      event.activity_driver_name || event.assignee_name || event.driver_name;
    const type = event.activity_type;
    switch (type) {
      case 'PARTIAL_RESCUE_WEIGHTS_INCREASED':
        message = (
          <Typography variant="body2">
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'donations.event-logs.PARTIAL_RESCUE_WEIGHTS_INCREASED',
                  {
                    name: event.actor_name,
                    food_type_name: foodTypes.find(
                      (type) => type.value === event.food_type_name
                    )?.title,
                  }
                ),
              }}
            />
          </Typography>
        );
        break;
      case 'PARTIAL_RESCUE_NEW_FOOD_TYPE_ADDED':
        message = (
          <Typography variant="body2">
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'donations.event-logs.PARTIAL_RESCUE_NEW_FOOD_TYPE_ADDED',
                  {
                    name: event.actor_name,
                    food_type_name: foodTypes.find(
                      (type) => type.value === event.food_type_name
                    )?.title,
                  }
                ),
              }}
            />
          </Typography>
        );
        break;
      case 'PARTIAL_RESCUE_ADJUST_ALLOCATION': {
        const changes = formatWeightChanges(event);
        message = (
          <Box>
            <Typography variant="body2">
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'donations.event-logs.PARTIAL_RESCUE_ADJUST_ALLOCATION',
                    {
                      actor_name: event.actor_name,
                      weight_difference: new Weight(
                        event.weight_difference ?? 0,
                        'lbs'
                      ).asRounded(event.weight_difference_unit ?? 'lbs'),
                      food_type_name: foodTypes.find(
                        (type) => type.value === event.food_type_name
                      )?.title,
                      status: event.is_over
                        ? t('donations.over')
                        : t('donations.under'),
                      unit: event.weight_difference_unit,
                    }
                  ),
                }}
              />
            </Typography>

            <Box ml={2}>
              <Typography color="text.secondary" variant="caption">
                {t('donations.the-following-changes-have-been-made')}
              </Typography>
              <Stack mt={1} direction="column" gap={1}>
                {changes.map((change) => {
                  return (
                    <>
                      {change.isAvailable ? (
                        <Typography variant="body2">
                          {t('donations.available-rescue-deleted')}
                        </Typography>
                      ) : (
                        <Stack direction="row" gap={0.5} key={change.label}>
                          <Typography variant="body2">
                            {t(`donations.${change.label}`)}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: 'line-through',
                              color: 'grey',
                            }}
                          >
                            {new Weight(change.change_from, 'lbs').asRounded(
                              event.estimated_weight_unit ?? 'lbs'
                            )}
                            {event.estimated_weight_unit ?? 'lbs'}
                          </Typography>
                          <ArrowRightAlt />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {new Weight(change.change_to, 'lbs').asRounded(
                              event.estimated_weight_unit ?? 'lbs'
                            )}
                            {event.estimated_weight_unit ?? 'lbs'}
                          </Typography>
                        </Stack>
                      )}
                    </>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        );
        break;
      }
      case 'PARTIAL_RESCUE_BOUNDS_AUTO_ADJUSTED': {
        const displayUnit = event.estimated_weight_unit ?? 'lbs';
        const foodTypeTitle = foodTypes.find(
          (type) => type.value === event.food_type_name
        )?.title;
        const minFrom = event.min_weight_change_from;
        const minTo = event.min_weight_change_to;
        const maxFrom = event.max_weight_change_from;
        const maxTo = event.max_weight_change_to;
        const minChanged =
          minFrom != null && minTo != null && Number(minFrom) !== Number(minTo);
        const maxChanged =
          maxFrom != null && maxTo != null && Number(maxFrom) !== Number(maxTo);

        message = (
          <Stack direction="column" gap={0.5}>
            {minChanged && (
              <Typography variant="body2">
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'donations.event-logs.PARTIAL_RESCUE_BOUNDS_AUTO_ADJUSTED_MIN',
                      {
                        food_type_name: foodTypeTitle,
                        from: new Weight(Number(minFrom), 'lbs').asRounded(
                          displayUnit
                        ),
                        to: new Weight(Number(minTo), 'lbs').asRounded(
                          displayUnit
                        ),
                        unit: displayUnit,
                      }
                    ),
                  }}
                />
              </Typography>
            )}
            {maxChanged && (
              <Typography variant="body2">
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'donations.event-logs.PARTIAL_RESCUE_BOUNDS_AUTO_ADJUSTED_MAX',
                      {
                        food_type_name: foodTypeTitle,
                        from: new Weight(Number(maxFrom), 'lbs').asRounded(
                          displayUnit
                        ),
                        to: new Weight(Number(maxTo), 'lbs').asRounded(
                          displayUnit
                        ),
                        unit: displayUnit,
                      }
                    ),
                  }}
                />
              </Typography>
            )}
          </Stack>
        );
        break;
      }
      case 'CREATED_DONATION':
        message = (
          <Typography variant="body2">
            {event.is_food_run
              ? t('donations.event-logs.CREATED_DONATION', {
                  name: event.assignor_name,
                  context: 'run',
                })
              : t('donations.event-logs.CREATED_DONATION', {
                  name: event.assignor_name,
                  context: 'donation',
                })}
          </Typography>
        );
        break;
      case 'DONATION_CHANGED':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.DONATION_CHANGED', {
              name: event.assignor_name,
            })}
          </Typography>
        );
        break;
      case 'RECEIVER_BULK_ASSIGN_BY_DONOR':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.RECEIVER_BULK_ASSIGN_BY_DONOR', {
              name: event.donor_name,
              title: event.pattern_title || event.donation_pattern_title,
            })}{' '}
            <Link to={viewOrg(event.assignee_team)} target="_blank">
              {event.pattern_assginee_team || event.assignee_business_name}
            </Link>
          </Typography>
        );

        break;
      case 'RECEIVER_ASSIGN_BY_DONOR':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.RECEIVER_ASSIGN_BY_DONOR', {
              context: 'assign',
              name:
                event.donor_business_name || event.pattern_donor_business_name,
            })}{' '}
            <Link to={viewOrg(event.assignee_team)} target="_blank">
              {event?.old_rescue_business_name ||
                event.assignee_business_name ||
                event.rescue_business_name}
            </Link>{' '}
            {t('donations.event-logs.RECEIVER_ASSIGN_BY_DONOR', {
              context: 'recipient',
            })}
          </Typography>
        );
        break;
      case 'DONATION_CONVERTED_TO_FOOD_RUN':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.DONATION_CONVERTED_TO_FOOD_RUN', {
              name: event.assignor_team_name,
              title: event.donation_title,
            })}
          </Typography>
        );
        break;
      case 'RESERVED': {
        const { onlyOneDriver, selfDriver, listDrivers } =
          getActivityDrivers(event);

        message = (
          <Typography variant="body2">
            {event.delivery_option === 'SELF-DELIVER' ? (
              t('donations.event-logs.RESERVED', {
                name: event.assignee_name,
                context: 'self_deliver',
              })
            ) : (
              <>
                {t('donations.event-logs.RESERVED', {
                  name: event.assignee_name,
                  context: onlyOneDriver ? 'single' : 'many',
                  driver_name: selfDriver
                    ? t('donations.itself')
                    : event.activity_rescue_drivers?.[0]?.name,
                })}
              </>
            )}
            {!onlyOneDriver && listDrivers}
          </Typography>
        );
        break;
      }
      case 'DRIVER_ASSIGN': {
        const { onlyOneDriver, selfDriver, listDrivers } =
          getActivityDrivers(event);

        message = (
          <Typography variant="body2">
            {t('donations.event-logs.DRIVER_ASSIGN', {
              context: onlyOneDriver ? 'single' : 'many',
              name: event.assignor_name,
              driver_name: selfDriver
                ? t('donations.itself')
                : event.activity_rescue_drivers?.[0]?.name,
            })}
            {!onlyOneDriver && listDrivers}
          </Typography>
        );
        break;
      }
      case 'DRIVER_UNASSIGN': {
        const { context, name, drivers } = describeDriverUnassign(event);
        if (!drivers.length || !name) {
          break;
        }

        message = (
          <Typography variant="body2">
            {t('donations.event-logs.DRIVER_UNASSIGN', {
              context,
              name,
              driver_name: drivers[0].name,
            })}
            {context === 'many' && (
              <>
                {' '}
                <Box component="span">
                  {drivers
                    .map(
                      (driver) =>
                        `${driver.name}${
                          String(driver.user_id) === String(event.actor_id)
                            ? ` (${t('donations.self')})`
                            : ''
                        }`
                    )
                    .join(', ')}
                </Box>
              </>
            )}
          </Typography>
        );
        break;
      }
      case 'RESCUE_STARTED':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.RESCUE_STARTED', {
              name: driverName,
            })}
          </Typography>
        );
        break;
      case 'RESCUE_CANCELLED_BY_RECEIVER':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.RESCUE_CANCELLED_BY_RECEIVER', {
              context: 'from',
              name: event.assignee_name,
            })}{' '}
            <Link to={viewOrg(event.assignee_team)} target="_blank">
              {event.assignee_business_name}
            </Link>{' '}
            {t('donations.event-logs.RESCUE_CANCELLED_BY_RECEIVER', {
              context: 'cancelled',
            })}{' '}
            {event.cancellation_reason
              ? t('donations.event-logs.RESCUE_CANCELLED_BY_RECEIVER', {
                  context: 'reason',
                  reason: event.cancellation_reason,
                })
              : ''}
          </Typography>
        );
        break;
      case 'BULK_FOOD_RUN_CHANGED':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.BULK_FOOD_RUN_CHANGED', {
              name: event.assignor_name,
            })}
          </Typography>
        );
        break;
      case 'FOOD_RUN_CHANGED':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.FOOD_RUN_CHANGED', {
              name: event.assignor_name,
            })}
          </Typography>
        );
        break;
      case 'FOOD_RUN_CLAIMED_SINGLE_INSTANCE':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.FOOD_RUN_CLAIMED_SINGLE_INSTANCE', {
              name: event.assignee_name,
            })}
          </Typography>
        );
        break;
      case 'FOOD_RUN_RECIPIENT_CHANGED_RETROACTIVELY':
        message = (
          <Typography variant="body2">
            {t(
              'donations.event-logs.FOOD_RUN_RECIPIENT_CHANGED_RETROACTIVELY',
              {
                name: event.assignor_name,
                oldRecipientName: event.activity_old_recipient_name,
                newRecipientName: event.activity_new_recipient_name,
              }
            )}
          </Typography>
        );
        break;
      case 'FOOD_RUN_PICKUP':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.FOOD_RUN_PICKUP', {
              name: event.assignee_name,
            })}
          </Typography>
        );
        break;
      case 'FOOD_RUN_DELIVERY':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.FOOD_RUN_DELIVERY', {
              name: event.assignee_name,
            })}
          </Typography>
        );
        break;
      case 'COMPLETED_RESCUE':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.COMPLETED_RESCUE', {
              name: event.assignee_name,
            })}
          </Typography>
        );
        break;
      case 'WEIGHTS_CHANGED':
        message = (
          <Typography variant="body2">
            {t('donations.event-logs.WEIGHTS_CHANGED', {
              name: event.assignee_name,
            })}
          </Typography>
        );
        break;
      case 'DRIVERS_CHANGED': {
        const { onlyOneDriver, selfDriver, listDrivers } =
          getActivityDrivers(event);
        const hasDriverList = event.activity_rescue_drivers.length > 0;

        message = (
          <Typography variant="body2">
            {t('donations.event-logs.DRIVERS_CHANGED', {
              name: event.assignee_name,
              context: hasDriverList
                ? onlyOneDriver
                  ? 'single'
                  : 'many'
                : undefined,
              driver_name: selfDriver
                ? t('donations.itself')
                : event.activity_rescue_drivers?.[0]?.name,
            })}
            {hasDriverList && !onlyOneDriver && listDrivers}
          </Typography>
        );
        break;
      }
      default:
        message = null;
    }
    return {
      activity_type: event.activity_type,
      created: event.created,
      message,
    };
  };
  const Log = ({ activity, isLast }) => {
    return (
      <Stack direction="row" spacing={1.5} sx={{ position: 'relative' }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: 30,
              justifyContent: 'center',
              width: 30,
            }}
          >
            <IconImg src={iconsTypeMap(activity.activity_type)} />
          </Box>
          {!isLast ? (
            <Box
              sx={{
                bgcolor: 'divider',
                flex: 1,
                minHeight: 18,
                my: 0.75,
                width: '1px',
              }}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            flex: 1,
            minWidth: 0,
            px: 1.5,
            py: 1.25,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatDisplayDate(moment.utc(activity.created).tz(tz), 'time')}
          </Typography>
          <Box
            sx={{
              mt: 0.25,
              '& a': {
                color: 'text.primary',
                fontWeight: 700,
                textDecoration: 'none',
              },
              '& a:hover': {
                color: 'text.primary',
                textDecoration: 'underline',
                textDecorationColor: 'currentColor',
              },
              '& .MuiTypography-root': {
                color: 'text.primary',
                lineHeight: 1.5,
              },
            }}
          >
            {activity.message}
          </Box>
        </Box>
      </Stack>
    );
  };

  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={180} />;
  }

  if (_isEmpty(data)) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {Object.keys(data).map((key) => {
        return (
          <Box key={key}>
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 1,
                color: 'text.secondary',
                display: 'inline-flex',
                fontWeight: 700,
                mb: 1.5,
                px: 1,
                py: 0.5,
              }}
            >
              {formatDisplayDate(moment(key), 'weekdayDateLong')}
            </Typography>
            <Stack spacing={0}>
              {data[key].map((event, index) => (
                <Log
                  activity={event}
                  isLast={index === data[key].length - 1}
                  key={`${event.activity_type}-${index}`}
                />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default DonationEventLogs;
